import React from 'react';
import { Language } from '../../pages/DevPortal';
import { Lock, ShieldAlert, CheckCircle2, Eye } from 'lucide-react';

export default function SecuritySection({ lang }: { lang: Language }) {
    const isEn = lang === 'en';

    return (
        <div className="animate-fade-in">
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
                {isEn ? 'Security Posture & Compliance' : 'Postura de Seguridad y Cumplimiento'}
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {isEn 
                    ? 'Security is built into every layer of Hospital Status. We operate on a defense-in-depth security model.'
                    : 'La seguridad está construida en cada capa de Hospital Status. Operamos con un modelo de defensa en profundidad.'}
            </p>

            <h2 className="text-xl font-bold text-slate-900 mb-6">
                {isEn ? 'Data Protection Controls' : 'Controles de Protección de Datos'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                <div className="border border-slate-200 rounded-lg p-5 bg-white">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                        <Lock className="w-4 h-4 text-emerald-700" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">
                        {isEn ? 'Encryption' : 'Cifrado'}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {isEn 
                            ? 'TLS 1.3 in transit via Cloudflare Edge. AES-256 for data at rest. PINs are salted and hashed using SHA-256 (Web Crypto API).'
                            : 'TLS 1.3 en tránsito vía Cloudflare Edge. AES-256 para datos en reposo. Los PINs son "salted" y hasheados con SHA-256.'}
                    </p>
                </div>
                
                <div className="border border-slate-200 rounded-lg p-5 bg-white">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                        <ShieldAlert className="w-4 h-4 text-indigo-700" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">
                        {isEn ? 'Rate Limiting' : 'Límites de Petición (Rate Limiting)'}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {isEn 
                            ? 'Brute force protection on login endpoints. 5 failed attempts trigger a 15-minute IP/Hospital block managed via Edge workers.'
                            : 'Protección contra fuerza bruta en endpoints de login. 5 intentos fallidos disparan un bloqueo de 15 minutos por IP/Hospital.'}
                    </p>
                </div>

                <div className="border border-slate-200 rounded-lg p-5 bg-white">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                        <CheckCircle2 className="w-4 h-4 text-blue-700" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">
                        {isEn ? 'Input Validation' : 'Validación de Entradas (Zod)'}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {isEn 
                            ? 'Zero-trust payload policy. All JSON payloads are validated against strict Zod schemas before hitting the database.'
                            : 'Política Zero-trust en payloads. Todo JSON es validado contra esquemas estrictos de Zod antes de tocar la base de datos.'}
                    </p>
                </div>

                <div className="border border-slate-200 rounded-lg p-5 bg-white">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                        <Eye className="w-4 h-4 text-purple-700" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">
                        {isEn ? 'Audit Logging' : 'Registro de Auditoría'}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {isEn 
                            ? 'Immutable audit table records critical actions (who, what, when) to ensure accountability for staff modifications.'
                            : 'Tabla de auditoría inmutable registra acciones críticas (quién, qué, cuándo) para asegurar responsabilidad compartida en modificaciones de staff.'}
                    </p>
                </div>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4 mt-12">
                {isEn ? 'Perimeter Security & Enterprise WAF' : 'Seguridad Perimetral y WAF Empresarial'}
            </h2>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
                <p className="text-slate-600 leading-relaxed mb-4">
                    {isEn 
                        ? 'To integrate peacefully with rigorous Enterprise healthcare networks, Hospital Status employs strict Perimeter network security designed to interface cleanly with "Security Zones" architectures like those of Oracle Cloud.'
                        : 'Para integrarse orgánicamente con redes hospitalarias corporativas, Hospital Status emplea una estricta seguridad perimetral diseñada para interactuar sin fricción con arquitecturas de "Security Zones" como las de Oracle Cloud.'}
                </p>
                <ul className="list-disc list-inside text-slate-600 space-y-2 ml-2">
                    <li>
                        <strong>{isEn ? 'Cloudflare Web Application Firewall (WAF):' : 'Web Application Firewall (WAF):'}</strong>{' '}
                        {isEn ? 'All incoming traffic passes through the Cloudflare WAF before reaching our API, instantly mitigating DDoS attacks and malicious payload injections.' : 'Todo el tráfico entrante pasa por el WAF de Cloudflare antes de tocar nuestra API, mitigando instantáneamente ataques DDoS (L3/L4/L7) e inyecciones maliciosas.'}
                    </li>
                    <li>
                        <strong>{isEn ? 'Zero Trust Architecture:' : 'Arquitectura Zero Trust:'}</strong>{' '}
                        {isEn ? 'Administrative interfaces (like this Portal and the Super-Admin HQ) are completely shielded from the public internet by Cloudflare Access Identity Proxies, requiring explicit OTP/SSO validation.' : 'Las interfaces administrativas están completamente resguardadas del internet público mediante Cloudflare Access, exigiendo validación estricta de identidad OTP/SSO antes de cargar la página inicial.'}
                    </li>
                </ul>
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-4">
                {isEn ? 'OWASP Top 10 Readiness' : 'Cumplimiento OWASP'}
            </h2>
            <div className="bg-slate-50 border border-slate-200 p-6 rounded-lg">
                <ul className="space-y-4 text-sm text-slate-700">
                    <li className="flex items-start gap-3">
                        <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                        <div>
                            <span className="font-bold block">A01: Broken Access Control</span>
                            <span className="text-slate-500">Mitigated by universal authMiddleware and explicit DB-level hospitalId scoping.</span>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                        <div>
                            <span className="font-bold block">A03: Injection</span>
                            <span className="text-slate-500">Mitigated by D1 parameterized queries native driver. No string concatenation parsing.</span>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="text-emerald-500 font-bold mt-0.5">✓</span>
                        <div>
                            <span className="font-bold block">A07: Identification and Authentication Failures</span>
                            <span className="text-slate-500">Mitigated by Double-PIN system, cryptographic hashes, and strict rate limits.</span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    );
}
